import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./LogUp.css"; // Using our dedicated CSS file for the sign-up form
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { authService } from '../../services';

// Named export for better practices
const LogUp = ({ onBackToLogin }) => {
	const navigate = useNavigate();
	const [isPasswordVisible, setIsPasswordVisible] = useState(false);
	const [signUpFormData, setSignUpFormData] = useState({
		username: '',
		email: '',
		password: '',
		marketing: false
	});
	const [validationErrors, setValidationErrors] = useState({});
	const [isSignUpLoading, setIsSignUpLoading] = useState(false);

	const togglePasswordVisibility = () => {
		setIsPasswordVisible(!isPasswordVisible);
	};

	const handleBackToLogin = () => {
		// Ensure scroll to top before navigation
		window.scrollTo(0, 0);
		
		if (onBackToLogin) {
			// If we have a callback prop (when rendered from Login page), use it
			onBackToLogin();
		} else {
			// If we're on a standalone signup page, navigate to login
			navigate("/login");
		}
	};

	// Handle input changes
	const handleSignUpInputChange = (e) => {
		const { name, value, type, checked } = e.target;
		setSignUpFormData(prev => ({
			...prev,
			[name]: type === 'checkbox' ? checked : value
		}));
		// Clear error when user starts typing
		if (validationErrors[name]) {
			setValidationErrors(prev => ({
				...prev,
				[name]: ''
			}));
		}
	};

	// Validate form
	const validateSignUpForm = () => {
		const newErrors = {};
		
		if (!signUpFormData.username) {
			newErrors.username = 'Username is required';
		} else if (signUpFormData.username.length < 3) {
			newErrors.username = 'Username must be at least 3 characters';
		}
		
		if (!signUpFormData.email) {
			newErrors.email = 'Email is required';
		} else if (!/\S+@\S+\.\S+/.test(signUpFormData.email)) {
			newErrors.email = 'Email is invalid';
		}
		
		if (!signUpFormData.password) {
			newErrors.password = 'Password is required';
		} else if (signUpFormData.password.length < 8) {
			newErrors.password = 'Password must be at least 8 characters';
		}
		
		setValidationErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	// Handle form submission
	const handleSignUpSubmit = async (e) => {
		e.preventDefault();
		
		if (!validateSignUpForm()) {
			return;
		}
		
		setIsSignUpLoading(true);
		
		try {
			// Call API để đăng ký
			const response = await authService.register({
				username: signUpFormData.username,
				email: signUpFormData.email,
				password: signUpFormData.password,
				marketing: signUpFormData.marketing
			});
			
			console.log('Sign up successful:', response);
			
			// If backend returned token, store it and user and navigate to explore
			if (response?.token) {
				try {
					localStorage.setItem('token', response.token);
					if (response.user) localStorage.setItem('user', JSON.stringify(response.user));
				} catch (e) {
					console.warn('Could not store auth token in localStorage:', e);
				}

				alert('Tài khoản đã được tạo và bạn đã được đăng nhập!');
				window.scrollTo(0, 0);
				navigate('/explore');
				return;
			}

			// Fallback: if no token returned, navigate to login page with pre-filled data
			alert('Tài khoản đã được tạo thành công! Bạn có thể đăng nhập ngay bây giờ.');
			navigate('/login', {
				state: {
					prefilledData: {
						username: signUpFormData.username,
						email: signUpFormData.email
					}
				}
			});
			
		} catch (error) {
			console.error('Sign up error:', error);
			console.error('Error details:', {
				message: error.message,
				response: error.response?.data,
				status: error.response?.status,
				statusText: error.response?.statusText
			});
			
			// Xử lý lỗi từ API
			let errorMessage = 'Đăng ký thất bại. Vui lòng thử lại.';
			
			if (error.response?.status === 400) {
				errorMessage = 'Dữ liệu đăng ký không hợp lệ.';
			} else if (error.response?.status === 409) {
				errorMessage = 'Email hoặc username đã tồn tại.';
			} else if (error.response?.data?.message) {
				errorMessage = error.response.data.message;
			} else if (typeof error === 'string') {
				errorMessage = error;
			}
			
			console.error('Final error message:', errorMessage);
			alert(errorMessage);
		} finally {
			setIsSignUpLoading(false);
		}
	};

	return (
		<div className="signup-modern-container">
			<Header />
			{/* Header with back button */}
			<div className="signup-modern-header">
				<button className="signup-modern-back-button" onClick={handleBackToLogin}>
					<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
					</svg>
					<span>Back to Login</span>
				</button>
			</div>

			{/* Main Content */}
			<main className="signup-modern-main-content">
				<div className="signup-modern-content-wrapper">
					{/* Logo and Title Section */}
					<div className="signup-modern-logo-section">
						<div className="signup-modern-logo-circle">
							<svg width="48" height="48" viewBox="0 0 48 48" fill="none">
								<circle cx="24" cy="24" r="20" fill="white" opacity="0.9"/>
								<path d="M24 12L28 20H32L26 26L28 34L24 30L20 34L22 26L16 20H20L24 12Z" fill="#667eea"/>
							</svg>
						</div>
						<h1 className="signup-modern-main-title">Tham gia cộng đồng của chúng tôi</h1>
						<p className="signup-modern-subtitle">Tạo tài khoản của bạn để bắt đầu</p>
					</div>

					{/* Sign Up Form Container */}
					<div className="signup-modern-form-container">
						<form className="signup-modern-form-content" onSubmit={handleSignUpSubmit}>
							<h2 className="signup-modern-form-title">Tạo tài khoản</h2>
							<p className="signup-modern-form-subtitle">Điền thông tin của bạn để tạo tài khoản</p>

							{/* Username Field */}
							<div className="signup-modern-input-group">
								<label className="signup-modern-input-label">Tên đăng nhập</label>
								<input 
									type="text" 
									name="username"
									value={signUpFormData.username}
									onChange={handleSignUpInputChange}
									className={`signup-modern-input-field ${validationErrors.username ? 'input-error' : ''}`}
									placeholder="Chọn tên đăng nhập"
								/>
								{validationErrors.username && <span className="signup-modern-error-message">{validationErrors.username}</span>}
							</div>

							{/* Email Field */}
							<div className="signup-modern-input-group">
								<label className="signup-modern-input-label">Địa chỉ email</label>
								<input 
									type="email" 
									name="email"
									value={signUpFormData.email}
									onChange={handleSignUpInputChange}
									className={`signup-modern-input-field ${validationErrors.email ? 'input-error' : ''}`}
									placeholder="Nhập email của bạn"
								/>
								{validationErrors.email && <span className="signup-modern-error-message">{validationErrors.email}</span>}
							</div>

							{/* Password Field */}
							<div className="signup-modern-input-group">
								<div className="signup-modern-password-header">
									<label className="signup-modern-input-label">Mật khẩu</label>
									<button 
										className="signup-modern-password-toggle"
										type="button"
										onClick={togglePasswordVisibility}
									>
										{isPasswordVisible ? (
											<svg width="20" height="20" viewBox="0 0 24 24" fill="none">
												<path d="M3 3L21 21M10.584 10.587C10.208 11.037 10 11.59 10 12.2C10 13.7 11.3 15 12.8 15C13.41 15 13.96 14.79 14.41 14.42M9.363 5.365C10.105 4.611 11.001 4.2 12 4.2C14.8 4.2 17 7.2 17 12.2C17 13.2 16.8 14.1 16.4 14.9M12 4.2C9.2 4.2 7 7.2 7 12.2C7 13.2 7.2 14.1 7.6 14.9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
											</svg>
										) : (
											<svg width="20" height="20" viewBox="0 0 24 24" fill="none">
												<path d="M1 12S5 4 12 4S23 12 23 12S19 20 12 20S1 12 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
												<circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
											</svg>
										)}
										<span className="signup-modern-password-toggle-text">
											{isPasswordVisible ? "Hide" : "Show"}
										</span>
									</button>
								</div>
								<input 
									type={isPasswordVisible ? "text" : "password"} 
									name="password"
									value={signUpFormData.password}
									onChange={handleSignUpInputChange}
									className={`signup-modern-input-field ${validationErrors.password ? 'input-error' : ''}`}
									placeholder="Tạo mật khẩu mạnh"
								/>
								{validationErrors.password && <span className="signup-modern-error-message">{validationErrors.password}</span>}
							</div>

							{/* Password Requirements */}
							<div className="signup-modern-password-requirements">
								<div className="signup-modern-requirement">
									<span>Use 8 or more characters</span>
								</div>
								<div className="signup-modern-requirement">
									<span>One uppercase character</span>
								</div>
								<div className="signup-modern-requirement">
									<span>One lowercase character</span>
								</div>
								<div className="signup-modern-requirement">
									<span>One special character</span>
								</div>
								<div className="signup-modern-requirement">
									<span>One number</span>
								</div>
							</div>

							{/* Marketing Checkbox */}
							<div className="signup-modern-checkbox-container">
								<input 
									type="checkbox" 
									name="marketing"
									id="marketing" 
									checked={signUpFormData.marketing}
									onChange={handleSignUpInputChange}
									className="signup-modern-checkbox" 
								/>
								<label htmlFor="marketing" className="signup-modern-checkbox-label">
									I want to receive emails about the product, feature updates, events, and marketing promotions.
								</label>
							</div>

							{/* Terms Text */}
							<div className="signup-modern-terms-text">
								By creating an account, you agree to the <a href="#" className="signup-modern-terms-link">Terms of use</a> and <a href="#" className="signup-modern-terms-link">Privacy Policy</a>.
							</div>

							{/* Create Account Button */}
							<button 
								type="submit" 
								className="signup-modern-create-btn"
								disabled={isSignUpLoading}
							>
								{isSignUpLoading ? (
									<>
										<svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
											<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
											<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
										</svg>
										Đang tạo tài khoản...
									</>
								) : (
									'Tạo tài khoản'
								)}
							</button>

							{/* Login Link */}
							<div className="signup-modern-login-link">
								Already have an account? <button 
									type="button"
									onClick={handleBackToLogin} 
									className="signup-modern-login-link-text"
								>
									Sign in
								</button>
							</div>
						</form>
					</div>

					{/* Footer Links */}
					<div className="signup-modern-footer-links">
						<div className="signup-modern-terms-footer">
							<div>Secure signup with reCAPTCHA subject to Google</div>
							<div className="signup-modern-terms-links-container">
								<a href="#" className="signup-modern-terms-link">Terms</a>
								<span className="signup-modern-terms-and">&</span>
								<a href="#" className="signup-modern-terms-link">Privacy</a>
							</div>
						</div>
					</div>
				</div>
			</main>
			<Footer />
		</div>
	);
};

export default LogUp;

